import { Mutex } from 'async-mutex';
import Delta from 'quill-delta';
import RecognitionHandler from 'libs/media-processing/recognition-handler';
import { capitalizeWord, trimStart } from './text-utils';
const DRAFT_MARKER_REGEX = /\[[nh]::[^\]]*\]/g;
const DRAFT_TEXT_COLOR = '#9e9e9e';
export class Recognition {
    constructor(editorController, messageStream, onHistoryLoaded, onTranscriptionStarted = null) {
        this.wasHistoryLoaded = false;
        this.currentDraft = null;
        this.handleTranscriptionComplete = async () => {
            // handleMessage ensures that this is executed after handling all messages
            await this.handleMessage(() => {
                this.clearDraft();
                this.currentDraft = null;
                this.flushPhraseBuffer();
                this.editorController.documentAligner.finalizePhrases(this.editorController.playback.duration);
                this.editorController.timestampLabels.update(0, this.editorController.getLength());
                this.cleanupEditor();
            }, []);
        };
        this.destroy = () => {
            this.recognitionHandler.destroy();
        };
        this.handleTranscriptionStarted = (startedMessageId, source) => {
            var _a;
            this.editorController.lastHandledMessageId = startedMessageId;
            if (source === 'socket') {
                this.editorController.clearForNewTranscription();
                (_a = this.onTranscriptionStarted) === null || _a === void 0 ? void 0 : _a.call(this);
            }
        };
        this.shouldSkipMessage = ({ id }) => (id <= this.editorController.lastHandledMessageId);
        this.handleMessage = async (handler, params) => {
            const release = await this.messageMutex.acquire();
            try {
                await handler(...params);
            }
            catch (error) {
                global.logger.error('handling message failed', {}, error);
            }
            finally {
                release();
            }
        };
        this.handleAddWord = (word, wsMessage) => {
            var _a, _b;
            if (this.shouldSkipMessage(wsMessage)) {
                return;
            }
            const currentLastConfirmedEndTime = (_b = (_a = this.currentDraft) === null || _a === void 0 ? void 0 : _a.lastConfirmedEndTime) !== null && _b !== void 0 ? _b : 0;
            const newLastConfirmedEndTime = Math.max(currentLastConfirmedEndTime, word.end);
            if (this.currentDraft !== null) {
                this.currentDraft = Object.assign(Object.assign({}, this.currentDraft), { lastConfirmedEndTime: newLastConfirmedEndTime });
            }
            else {
                this.currentDraft = {
                    text: '',
                    fragments: [],
                    range: null,
                    lastConfirmedEndTime: newLastConfirmedEndTime,
                };
            }
            this.editorController.lastHandledMessageId = wsMessage.id;
            this.editorController.lastHandledPhraseTime = word.end;
            this.phraseBuffer.push(word);
            if (wsMessage.source === 'socket') {
                const { lastConfirmedEndTime, fragments } = this.currentDraft;
                this.clearDraft();
                this.currentDraft = {
                    text: '',
                    fragments,
                    range: null,
                    lastConfirmedEndTime,
                };
                this.flushPhraseBuffer();
                if (!this.wasHistoryLoaded) {
                    this.onHistoryLoaded();
                }
                this.wasHistoryLoaded = true;
            }
            this.updateDraft();
        };
        this.handleAddSpeaker = (diarizationCode, wsMessage) => {
            if (this.shouldSkipMessage(wsMessage)) {
                return;
            }
            this.flushPhraseBuffer();
            this.clearDraft();
            if (this.currentDraft !== null) {
                this.currentDraft = {
                    text: '',
                    fragments: [],
                    range: null,
                    lastConfirmedEndTime: this.currentDraft.lastConfirmedEndTime,
                };
            }
            this.editorController.speakers.createSpeakerOnEnd(diarizationCode);
        };
        this.handleSpeakerId = (xmlSpeaker, wsMessage) => {
            if (this.shouldSkipMessage(wsMessage)) {
                return;
            }
            this.editorController.speakers.speakerIdentification
                .fixTemporarySpeaker(xmlSpeaker);
        };
        this.handleDraft = (fragments) => {
            const nextFragments = this.currentDraft !== null && fragments.length > 0
                ? this.mergeDraftFragments(this.currentDraft.fragments, fragments)
                : fragments;
            if (this.currentDraft !== null) {
                this.currentDraft = Object.assign(Object.assign({}, this.currentDraft), { fragments: nextFragments });
            }
            else {
                this.currentDraft = {
                    text: '',
                    fragments: nextFragments,
                    range: null,
                    lastConfirmedEndTime: 0,
                };
            }
            this.updateDraft();
        };
        this.handleAddHeading = (headingData, wsMessage) => {
            if (this.shouldSkipMessage(wsMessage)) {
                return;
            }
            this.flushPhraseBuffer();
            this.editorController.sections.createHeadingOnEnd(headingData);
        };
        this.cleanupEditor = () => {
            const lastCharacterIndex = this.editorController.getLength() - 1;
            const lastButOneBlot = this.editorController.getBlock(lastCharacterIndex).prev;
            const isLastButOneIndexParagraphEnd = this.editorController.getText(lastCharacterIndex - 1, 1) === '\n';
            // Removing redundant last speaker except if there is only one speaker
            if (lastButOneBlot !== null
                && lastButOneBlot.domNode.tagName === 'H4'
                && isLastButOneIndexParagraphEnd) {
                this.editorController.execTextChange({ requestSave: false }, () => {
                    this.editorController.speakers.removeSpeaker(lastButOneBlot, 'api');
                    // NOTE: delete also the line end left behind by the speaker
                    this.editorController.deleteText(this.editorController.getLength() - 1, 1, 'api');
                });
            }
        };
        this.editorController = editorController;
        this.recognitionHandler = new RecognitionHandler(messageStream);
        this.onHistoryLoaded = onHistoryLoaded;
        this.onTranscriptionStarted = onTranscriptionStarted;
        this.messageMutex = new Mutex();
        this.phraseBuffer = [];
        this.recognitionHandler.addRecognitionListener('word', (...params) => this.handleMessage(this.handleAddWord, params));
        this.recognitionHandler.addRecognitionListener('speaker', (...params) => this.handleMessage(this.handleAddSpeaker, params));
        this.recognitionHandler.addRecognitionListener('speaker-id', (...params) => this.handleMessage(this.handleSpeakerId, params));
        this.recognitionHandler.addRecognitionListener('draft', (...params) => this.handleMessage(this.handleDraft, params));
        this.recognitionHandler.addRecognitionListener('heading', (...params) => this.handleMessage(this.handleAddHeading, params));
        this.recognitionHandler.addRecognitionListener('transcription-started', (startedMessageId, source) => (this.handleTranscriptionStarted(startedMessageId, source)));
    }
    static fragmentsOverlap(a, b) {
        const ab = a.begin;
        const ae = a.end;
        const bb = b.begin;
        const be = b.end;
        if (ab == null || ae == null || bb == null || be == null) {
            return false;
        }
        return ab < be && bb < ae;
    }
    mergeDraftFragments(existing, incoming) {
        const merged = incoming.reduce((acc, inv) => {
            const filtered = acc.filter((ex) => !Recognition.fragmentsOverlap(ex, inv));
            filtered.push(inv);
            return filtered;
        }, [...existing]);
        return merged.sort((a, b) => {
            var _a, _b;
            const ab = (_a = a.begin) !== null && _a !== void 0 ? _a : 0;
            const bb = (_b = b.begin) !== null && _b !== void 0 ? _b : 0;
            return ab - bb;
        });
    }
    updateDraft() {
        if (this.currentDraft === null) {
            return;
        }
        const { fragments, lastConfirmedEndTime } = this.currentDraft;
        const fragmentsToShow = fragments
            .filter((frag) => (frag.begin === null
            || frag.begin >= lastConfirmedEndTime
            || (frag.end !== null && frag.end > lastConfirmedEndTime)))
            .map((frag) => {
            var _a;
            return ({
                begin: frag.begin,
                end: frag.end,
                text: ((_a = frag.text) !== null && _a !== void 0 ? _a : '').replace(DRAFT_MARKER_REGEX, ''),
            });
        })
            .filter((frag) => frag.text.length > 0);
        const combinedDraft = fragmentsToShow
            .map((frag) => frag.text)
            .join(' ');
        if (combinedDraft.length === 0) {
            if (this.currentDraft.range !== null) {
                this.clearDraft();
            }
            this.currentDraft = Object.assign(Object.assign({}, this.currentDraft), { text: '' });
            return;
        }
        if (combinedDraft === this.currentDraft.text) {
            return;
        }
        this.clearDraft();
        this.displayDraft(combinedDraft);
        this.currentDraft = Object.assign(Object.assign({}, this.currentDraft), { text: combinedDraft });
    }
    displayDraft(text) {
        this.editorController.execTextChange({ updateMetadata: false, runAligner: false, requestSave: false }, () => {
            const documentLength = this.editorController.getLength();
            const insertIndex = documentLength - 1;
            let textToInsert = text;
            if (insertIndex > 0) {
                const lastChar = this.editorController.getText(insertIndex - 1, 1);
                const needsLeadingSpace = lastChar !== '' && lastChar !== '\n' && lastChar !== ' ';
                if (needsLeadingSpace) {
                    textToInsert = ` ${text}`;
                }
            }
            const delta = new Delta();
            if (insertIndex > 0) {
                delta.retain(insertIndex);
            }
            delta.insert(textToInsert, {
                color: DRAFT_TEXT_COLOR,
            });
            this.editorController.updateContents(delta, 'api');
            if (this.currentDraft !== null) {
                this.currentDraft = Object.assign(Object.assign({}, this.currentDraft), { range: { index: insertIndex, length: textToInsert.length } });
            }
        });
    }
    clearDraft() {
        if (this.currentDraft === null || this.currentDraft.range === null) {
            return;
        }
        const { index, length } = this.currentDraft.range;
        this.currentDraft = Object.assign(Object.assign({}, this.currentDraft), { range: null });
        this.editorController.execTextChange({ updateMetadata: false, runAligner: false, requestSave: false }, () => {
            const delta = new Delta()
                .retain(index)
                .delete(length);
            this.editorController.updateContents(delta, 'api');
        });
    }
    flushPhraseBuffer() {
        const wordsToAdd = this.phraseBuffer.splice(0, this.phraseBuffer.length);
        this.addWords(wordsToAdd);
    }
    fixFirstWord(event) {
        let text = capitalizeWord(event.text).word;
        text = trimStart(text);
        // eslint-disable-next-line no-param-reassign
        event.text = text;
    }
    addWords(events) {
        if (events.length === 0)
            return;
        this.editorController.execTextChange({ updateMetadata: false, runAligner: false, requestSave: false }, () => {
            const documentLength = this.editorController.getLength();
            const isFirstInLine = this.editorController.getText(this.editorController.getLength() - 2, 1) === '\n';
            if (documentLength <= 1 || isFirstInLine) {
                this.fixFirstWord(events[0]);
            }
            const delta = new Delta();
            if (documentLength > 1) {
                // insert before the terminating line end.
                // Avoid retain of length 0 - it causes quill to crash.
                delta.retain(documentLength - 1);
            }
            let timestampIndex = documentLength - 2; // index of the last letter of the last line
            for (let i = 0; i < events.length; i += 1) {
                const { begin, end, text, isNoise, } = events[i];
                this.editorController.documentAligner.loadPhrase(text, begin, end);
                if (!isNoise && text.length > 0) {
                    delta.insert(text, {
                        speaker: false,
                    });
                    timestampIndex += text.length;
                    this.editorController.textMetadata.addTimestampAtEndOnIndex(timestampIndex, begin, end);
                }
            }
            this.editorController.updateContents(delta, 'api');
        });
    }
}
